import { ReactNode } from 'react';
import { ButtonView, ButtonViewProps } from '@shared/components/View';
import { useMenuContext } from './MenuContext';
import { RowContent, RowContentProps } from './RowContent';
import styles from './MenuButton.module.css';

/**
 * Menu.Button
 *
 * A pre-styled trigger button that toggles the menu. Shows an optional
 * down-arrow indicator and an active state while the menu is open. Use this
 * when you want a standard button trigger; use `Menu.Trigger` when you need to
 * wrap a custom element instead.
 */

interface MenuButtonProps extends Omit<ButtonViewProps, 'label'>, RowContentProps {
  children?: ReactNode;
  showDownArrow?: boolean;
  compact?: boolean;
  round?: boolean;
  primary?: boolean;
}

export function MenuButton({
  children,
  label,
  startContent,
  endContent,
  icon,
  iconSize,
  inactive,
  danger,
  className,
  style,
  showDownArrow = true,
  compact = false,
  round = false,
  primary = false,
  ...rest
}: MenuButtonProps) {
  const { toggle, isOpen, anchorName } = useMenuContext();

  return (
    <ButtonView
      onClick={toggle}
      style={{ anchorName, ...style }}
      aria-label="Open menu"
      aria-haspopup="menu"
      aria-expanded={isOpen || undefined}
      data-primary={primary || undefined}
      data-compact={compact || undefined}
      data-round={round || undefined}
      data-danger={danger || undefined}
      data-show-arrow={showDownArrow || undefined}
      className={[styles.menuButton, className]}
      {...rest}
    >
      <RowContent
        label={label}
        startContent={startContent}
        endContent={endContent}
        icon={icon}
        iconSize={iconSize}
        danger={danger}
        inactive={inactive}
      >
        {children}
      </RowContent>
    </ButtonView>
  );
}
